/**
 * 営業案件フォームのバリデーション
 */
export interface ValidationErrors {
    [key: string]: string;
}

/**
 * 取引先（client_id）の入力が必須となるフェーズかを判定
 * フェーズ先頭数字が 2 以上（=「2.アポイント/買付提出」以降）で必須
 */
export const isClientRequiredForPhase = (phase: string | undefined): boolean => {
    const phaseNumber = parseInt(phase || '', 10);
    return !isNaN(phaseNumber) && phaseNumber >= 2;
};

// DB 側で「未紐付け」を示すセンチネル値（Zero UUID）
const ZERO_UUID = '00000000-0000-0000-0000-000000000000';

/**
 * client_id が顧客マスタへの有効な紐付けかを判定
 * null/undefined/空文字 と Zero UUID は「未紐付け」扱い
 */
export const hasValidClientId = (clientId: any): boolean => {
    if (!clientId) return false;
    if (typeof clientId === 'string' && clientId.trim() === ZERO_UUID) return false;
    return true;
};

export const validateSalesProject = (formData: any): ValidationErrors => {
    const errors: ValidationErrors = {};

    // 案件名: 必須
    if (!formData.project_name || formData.project_name.trim() === '') {
        errors.project_name = '案件名は必須入力です。';
    }

    // 取引先: フェーズ「2.」以降の場合のみ顧客マスタからの選択必須
    if (isClientRequiredForPhase(formData.phase) && !hasValidClientId(formData.client_id)) {
        errors.client_id = '取引先は顧客マスタから選択してください。';
    }

    // 不動産業者(管理・仲介): 入力がある場合はマスタ選択必須
    if (formData.real_estate_agency_mgmt && !hasValidClientId(formData.real_estate_agency_mgmt_id)) {
        errors.real_estate_agency_mgmt_id = '不動産業者(管理・仲介)は顧客マスタから選択してください。';
    }

    // 不動産業者(売却仲介): 入力がある場合はマスタ選択必須
    if (formData.real_estate_agency_brokerage && !hasValidClientId(formData.real_estate_agency_brokerage_id)) {
        errors.real_estate_agency_brokerage_id = '不動産業者(売却仲介)は顧客マスタから選択してください。';
    }

    // 案件種別: 必須（未分類は 2026-11-02 に解消し、以後は作らない）
    if (!formData.project_type) {
        errors.project_type = '案件種別（仕入 / 売上）を選択してください。';
    }

    // 親案件: 名前の表示があるのにIDが未確定（不整合）の場合はエラー
    if (formData.project_type === 'sales' && formData.parent_project_name && !formData.parent_project_id) {
        errors.parent_project_id = '親案件は検索結果から選択してください。';
    }

    // 受注確定日: フェーズ「6.受注(契約)/決済」(Sランク) で保存する場合は必須
    // 受注実績の起算日（受注該当日）として扱うため、S確定のタイミングで入力を担保する
    if ((formData.phase || '').startsWith('6.') && !formData.order_confirmed_date) {
        errors.order_confirmed_date = formData.project_type === 'purchase'
            ? 'フェーズ6（受注/決済）で保存するには決済日の入力が必須です。'
            : 'フェーズ6（受注/決済）で保存するには受注確定日の入力が必須です。';
    }

    return errors;
};

// 日本国内電話番号 (種別ごとに桁数を判定)
//   携帯 (070/080/090) … 11桁
//   IP電話 (050)        … 11桁
//   フリーダイヤル 0800 … 11桁
//   その他 0 始まり     … 10桁 (固定電話・0120 など)
const JP_PHONE_PATTERNS: ReadonlyArray<RegExp> = [
    /^0[789]0\d{8}$/,
    /^050\d{8}$/,
    /^0800\d{7}$/,
    /^0\d{9}$/,
];

const CONTAINS_FULLWIDTH_DIGIT = /[０-９]/;

export const validateJapanesePhone = (value: string | undefined | null): string | null => {
    if (!value || value.trim() === '') return null;
    if (CONTAINS_FULLWIDTH_DIGIT.test(value)) {
        return '電話番号は半角数字で入力してください。';
    }
    const digits = value.replace(/[-\s]/g, '');
    if (!/^\d+$/.test(digits)) {
        return '電話番号は半角数字で入力してください（ハイフン可）。';
    }
    if (JP_PHONE_PATTERNS.some((re) => re.test(digits))) return null;
    return '電話番号の形式が正しくありません（例: 03-1234-5678 / 090-1234-5678）。';
};

/**
 * フリガナのカタカナ統一変換
 * ひらがな→カタカナ、半角カナ→全角カナ (NFKC で濁点合成も吸収)
 */
export const toKatakana = (value: string): string => {
    const nfkc = value.normalize('NFKC');
    return nfkc.replace(/[ぁ-ゖ]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) + 0x60));
};

// カタカナ変換後も残ってはいけない文字 (漢字・ひらがな)
const KANA_INVALID_CHARS = /[一-龯ぁ-ゖ]/;

/**
 * フリガナ欄の検証 (空は許容)
 * blur 時の自動変換 (toKatakana) を前提に、変換後も漢字等が残る場合のみエラー
 */
export const validateKanaField = (value: string | undefined | null): string | null => {
    if (!value || value.trim() === '') return null;
    if (KANA_INVALID_CHARS.test(toKatakana(value))) {
        return 'フリガナはカタカナで入力してください。';
    }
    return null;
};

// 複数連絡先 1 件分の値検証 (tel / mobile / fax は電話番号形式、email は簡易メール形式)
export const validateContactValue = (
    kind: 'tel' | 'mobile' | 'fax' | 'email',
    value: string | undefined | null,
): string | null => {
    if (!value || value.trim() === '') return null;
    if (kind === 'email') {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) ? null : 'メールアドレスの形式が正しくありません。';
    }
    return validateJapanesePhone(value);
};

export const validateCrmCustomer = (
    formData: any,
    customerType: 'individual' | 'corporate',
): ValidationErrors => {
    const errors: ValidationErrors = {};
    // 事業者登録番号 (インボイス登録番号) は法人・個人共通。任意だが入力時は T+13 桁のみ許可
    const inv = (formData.invoice_reg_no || '').trim();
    if (inv && !/^T\d{13}$/.test(inv)) {
        errors.invoice_reg_no = '事業者登録番号は T + 数字13桁で入力してください。';
    }
    if (customerType === 'individual') {
        // 複数連絡先 (personal / business) の値をまとめて検証し、最初のエラーを contacts に集約する
        const contacts: any[] = Array.isArray(formData.contacts) ? formData.contacts : [];
        for (const c of contacts) {
            const ce = validateContactValue(c.contact_kind, c.value);
            if (ce) {
                errors.contacts = ce;
                break;
            }
        }

        const lk = validateKanaField(formData.last_name_kana);
        if (lk) errors.last_name_kana = lk;
        const fk = validateKanaField(formData.first_name_kana);
        if (fk) errors.first_name_kana = fk;

        // 勤務先法人のバリデーション: 名前があるのにIDがない（未連携）場合はエラー
        if (formData.employer_name && !hasValidClientId(formData.employer_id)) {
            errors.employer_id = '勤務先法人は顧客マスタから選択してください。';
        }
    } else {
        // 電話・FAX は複数連絡先で管理するため、各行の値をまとめて検証する
        const corpContacts: any[] = Array.isArray(formData.contacts) ? formData.contacts : [];
        for (const c of corpContacts) {
            const ce = validateContactValue(c.contact_kind, c.value);
            if (ce) {
                errors.contacts = ce;
                break;
            }
        }

        const ck = validateKanaField(formData.company_name_kana);
        if (ck) errors.company_name_kana = ck;

        // URL は任意。入力時は http(s):// 始まりまたはドメイン形式のみ許可 (空白・全角は不可)
        const url = (formData.website_url || '').trim();
        if (url && !/^(https?:\/\/)?[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+(:\d+)?(\/[^\s]*)?$/.test(url)) {
            errors.website_url = 'URL の形式が正しくありません（例: https://www.example.co.jp）。';
        }
    }
    return errors;
};
